import '@fortawesome/fontawesome-free/css/all.min.css';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import ScrollToTop from './components/common/ScrollToTop';
import useSessionCheck from './hooks/useSessionCheck';
import useIsMobile from './hooks/useIsMobile';
import ProtectedRoute from './components/common/ProtectedRoute';
import PublicLayout from './layouts/PublicLayout';
import AdminLayout from './layouts/AdminLayout';

// Public pages
import HomePage from './pages/public/HomePage';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import SeedingDownload from './pages/public/SeedingDownload';

// Auth pages
import SuperAdminLoginPage from './pages/auth/SuperAdminLoginPage';
import AdminLoginPage from './pages/auth/AdminLoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// SuperAdmin pages
import Dashboard from './pages/superadmin/Dashboard';
import ShopsManagement from './pages/superadmin/ShopsManagement';
import Statistics from './pages/superadmin/Statistics';
import UsersManagement from './pages/superadmin/UsersManagement';
import Notes from './pages/superadmin/Notes';

// Admin (chủ cửa hàng) pages
import AdminDashboard from './pages/admin/AdminDashboard';
import BillList from './pages/admin/bills/BillList';
import BillDetail from './pages/admin/bills/BillDetail';
import ServingBills from './pages/admin/bills/ServingBills';
import WaitingCustomers from './pages/admin/bills/WaitingCustomers';
import CreateOrder from './pages/admin/bills/CreateOrder';
import CustomerList from './pages/admin/customers/CustomerList';
import TodayCustomers from './pages/admin/customers/TodayCustomers';
import CustomerCreate from './pages/admin/customers/CustomerCreate';
import CustomerDetail from './pages/admin/customers/CustomerDetail';
import CustomerBuyHistory from './pages/admin/customers/CustomerBuyHistory';
import CustomerPointHistory from './pages/admin/customers/CustomerPointHistory';
import ServiceList from './pages/admin/services/ServiceList';
import ServiceCreate from './pages/admin/services/ServiceCreate';
import ServiceDetail from './pages/admin/services/ServiceDetail';
import ServiceEdit from './pages/admin/services/ServiceEdit';
import ServiceVariantCreate from './pages/admin/services/ServiceVariantCreate';
import PackageEditor from './pages/admin/packages/PackageEditor';
import EmployeeList from './pages/admin/employees/EmployeeList';
import EmployeeCreate from './pages/admin/employees/EmployeeCreate';
import EmployeeDetail from './pages/admin/employees/EmployeeDetail';
import ReportDashboard from './pages/admin/reports/ReportDashboard';
import ReportCustomer from './pages/admin/reports/ReportCustomer';
import ReportRevenue from './pages/admin/reports/ReportRevenue';
import ReportEndDay from './pages/admin/reports/ReportEndDay';
import ReportSalesDetail from './pages/admin/reports/ReportSalesDetail';
import ReportBillsDetail from './pages/admin/reports/ReportBillsDetail';
import RevenueBookIndex from './pages/admin/reports/RevenueBookIndex';
import RevenueBookS1a from './pages/admin/reports/RevenueBookS1a';
import CommissionList from './pages/admin/commissions/CommissionList';
import PointRuleList from './pages/admin/points/PointRuleList';
import AttributeList from './pages/admin/attributes/AttributeList';
import FeedbackIndex from './pages/admin/feedback/FeedbackIndex';
import FeedbackQr from './pages/admin/feedback/FeedbackQr';
import FeedbackReviews from './pages/admin/feedback/FeedbackReviews';
import FeedbackQrDetail from './pages/admin/feedback/FeedbackQrDetail';
import ShiftList from './pages/admin/shifts/ShiftList';
import SalaryTable from './pages/admin/salary/SalaryTable';
import SalaryDetail from './pages/admin/salary/SalaryDetail';
import BankingPage from './pages/admin/banking/BankingPage';
import ReportSales from './pages/admin/reports/ReportSales';
import SettingsIndex from './pages/admin/settings/SettingsIndex';
import InvoiceSettings from './pages/admin/settings/InvoiceSettings';
import PrinterSettings from './pages/admin/settings/PrinterSettings';
import ShopSettings from './pages/admin/settings/ShopSettings';
import ChangePassword from './pages/admin/settings/ChangePassword';
import ServiceTabsSettings from './pages/admin/settings/ServiceTabsSettings';

// Checkin (màn khách tự check-in)
import CheckinPage from './pages/checkin/CheckinPage';

function AppInner() {
    useSessionCheck();
    return null;
}

// Tablet: cạnh ngắn của màn hình >= 600px (điện thoại kể cả xoay ngang đều < 600px)
const isTabletScreen = () =>
    typeof window !== 'undefined' && Math.min(window.screen.width, window.screen.height) >= 600;

// Chặn đăng nhập/đăng ký trên điện thoại — chỉ cho phép máy tính và tablet (dùng làm màn check-in).
// Trên điện thoại, điều hướng về trang chủ.
function DesktopOnlyRoute({ children }) {
    const isMobile = useIsMobile();
    if (isMobile && !isTabletScreen()) {
        return <Navigate to="/trang-chu" replace />;
    }
    return children;
}

function App() {
    return (
        <BrowserRouter>
            <AppInner />
            <ScrollToTop />
            <Routes>
                {/* ==================== Public Routes ==================== */}
                <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
                <Route path="/trang-chu" element={<PublicLayout><HomePage /></PublicLayout>} />
                <Route path="/chinh-sach-bao-mat" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
                <Route path="/tai-app" element={<SeedingDownload />} />

                {/* ==================== SuperAdmin Routes ==================== */}
                <Route path="/navo-admin/login" element={<SuperAdminLoginPage />} />
                <Route
                    path="/navo-admin/dashboard"
                    element={<ProtectedRoute allowedRoles={['superadmin']}><Dashboard /></ProtectedRoute>}
                />
                <Route
                    path="/navo-admin/shops"
                    element={<ProtectedRoute allowedRoles={['superadmin']}><ShopsManagement /></ProtectedRoute>}
                />
                <Route
                    path="/navo-admin/users"
                    element={<ProtectedRoute allowedRoles={['superadmin']}><UsersManagement /></ProtectedRoute>}
                />
                <Route
                    path="/navo-admin/statistics"
                    element={<ProtectedRoute allowedRoles={['superadmin']}><Statistics /></ProtectedRoute>}
                />
                <Route
                    path="/navo-admin/notes"
                    element={<ProtectedRoute allowedRoles={['superadmin']}><Notes /></ProtectedRoute>}
                />

                {/* ==================== Checkin Route ==================== */}
                <Route
                    path="/checkin"
                    element={
                        <ProtectedRoute allowedRoles={['admin', 'manage', 'employee', 'checkin']} redirectTo="/admin/login">
                            <CheckinPage />
                        </ProtectedRoute>
                    }
                />

                {/* ==================== Admin (Chủ cửa hàng) Routes ==================== */}
                <Route path="/admin/login" element={<DesktopOnlyRoute><AdminLoginPage /></DesktopOnlyRoute>} />
                <Route path="/admin/register" element={<DesktopOnlyRoute><RegisterPage /></DesktopOnlyRoute>} />
                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute allowedRoles={['admin', 'manage', 'employee']} redirectTo="/admin/login">
                            <AdminLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="bills" element={<BillList />} />
                    <Route path="bills/create" element={<CreateOrder />} />
                    <Route path="bills/waiting" element={<WaitingCustomers />} />
                    <Route path="bills/:id" element={<BillDetail />} />
                    <Route path="serving" element={<ServingBills />} />
                    <Route path="customers" element={<CustomerList />} />
                    <Route path="today-customers" element={<TodayCustomers />} />
                    <Route path="customers/create" element={<CustomerCreate />} />
                    <Route path="customers/:id" element={<CustomerDetail />} />
                    <Route path="customers/:id/history" element={<CustomerBuyHistory />} />
                    <Route path="customers/:id/points" element={<CustomerPointHistory />} />
                    <Route path="services" element={<ServiceList />} />
                    <Route path="services/create" element={<ServiceCreate />} />
                    <Route path="services/:id" element={<ServiceDetail />} />
                    <Route path="services/:id/edit" element={<ServiceEdit />} />
                    <Route path="services/:id/create-variant" element={<ServiceVariantCreate />} />
                    <Route path="packages/create" element={<PackageEditor />} />
                    <Route path="packages/:id/edit" element={<PackageEditor />} />
                    <Route path="employees" element={<EmployeeList />} />
                    <Route path="employees/create" element={<EmployeeCreate />} />
                    <Route path="employees/:id" element={<EmployeeDetail />} />
                    <Route path="reports" element={<ReportDashboard />} />
                    <Route path="reports/customers" element={<ReportCustomer />} />
                    <Route path="reports/revenue" element={<ReportRevenue />} />
                    <Route path="reports/end-day" element={<ReportEndDay />} />
                    <Route path="reports/products" element={<ReportSales />} />
                    <Route path="reports/products-detail" element={<ReportSalesDetail />} />
                    <Route path="reports/bills-detail" element={<ReportBillsDetail />} />
                    <Route path="revenue-book" element={<RevenueBookIndex />} />
                    <Route path="revenue-book/s1a-hkd" element={<RevenueBookS1a />} />
                    <Route path="commissions" element={<CommissionList />} />
                    <Route path="points" element={<PointRuleList />} />
                    <Route path="attributes" element={<AttributeList />} />
                    <Route path="feedback" element={<FeedbackIndex />} />
                    <Route path="feedback/qr" element={<FeedbackQr />} />
                    <Route path="feedback/reviews" element={<FeedbackReviews />} />
                    <Route path="feedback/qr/:billId" element={<FeedbackQrDetail />} />
                    <Route path="shifts" element={<ShiftList />} />
                    <Route path="salary" element={<SalaryTable />} />
                    <Route path="salary/:employeeId" element={<SalaryDetail />} />
                    <Route path="banking" element={<BankingPage />} />
                    <Route path="settings" element={<SettingsIndex />} />
                    <Route path="settings/shop" element={<ShopSettings />} />
                    <Route path="settings/change-password" element={<ChangePassword />} />
                    <Route path="settings/invoice" element={<InvoiceSettings />} />
                    <Route path="settings/printer" element={<PrinterSettings />} />
                    <Route path="settings/service-tabs" element={<ServiceTabsSettings />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}

export default App;
